import { useMemo, useState } from 'react';
import { useDisputes, useClawbacks } from './hooks/useDisputes';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useMerchants } from '../merchants/hooks/useMerchants';
import { DisputeStatusBadge } from './DisputeStatusBadge';
import { RefundModal } from './RefundModal';
import { formatMinor } from '../../shared/utils/money';
import Button from '../../shared/components/Button';
import type { DisputeStatus } from './types';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Under Review', value: 'UNDER_REVIEW' },
  { label: 'Won', value: 'WON' },
  { label: 'Lost', value: 'LOST' },
];

type Tab = 'disputes' | 'clawbacks';

export default function Disputes() {
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<Tab>('disputes');
  const [statusFilter, setStatusFilter] = useState('');
  const [showRefund, setShowRefund] = useState(false);

  const { data: merchantsData } = useMerchants();
  const merchantMap = useMemo(
    () => new Map<string, string>((merchantsData ?? []).map((m) => [m.id, m.name])),
    [merchantsData],
  );
  const merchantName = (tenantId: string) => merchantMap.get(tenantId) || tenantId;

  const disputesQuery = useDisputes(statusFilter || undefined);
  const clawbacksQuery = useClawbacks();

  const canRefund = hasPermission(Permission.PAYMENT_REFUND);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Disputes</h1>
        {canRefund && <Button onClick={() => setShowRefund(true)}>Refund Payment</Button>}
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-border mb-4">
        {([['disputes', 'Disputes'], ['clawbacks', 'Clawback Liabilities']] as [Tab, string][]).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`pb-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
              tab === value ? 'border-[#28282A] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'disputes' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === f.value ? 'bg-[#28282A] text-white' : 'bg-muted text-muted-foreground hover:bg-muted'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {disputesQuery.isLoading && <div className="text-center py-16 text-muted-foreground">Loading disputes…</div>}
          {disputesQuery.error && <div className="text-center py-16 text-red-500">Failed to load disputes.</div>}
          {!disputesQuery.isLoading && !disputesQuery.error && (disputesQuery.data?.length ?? 0) === 0 && (
            <div className="text-center py-16 text-muted-foreground">No disputes found.</div>
          )}

          {!disputesQuery.isLoading && !disputesQuery.error && (disputesQuery.data?.length ?? 0) > 0 && (
            <div className="bg-card rounded-2xl border border-border overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Provider Ref</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Provider</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Amount</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Reason</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Opened</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Resolved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {disputesQuery.data!.map((d) => (
                    <tr key={d.id} className="hover:bg-muted transition-colors">
                      <td className="px-6 py-4 text-sm text-foreground font-mono">{d.providerRef || '—'}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground max-w-[160px] truncate" title={d.tenantId}>{merchantName(d.tenantId)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{d.provider}</td>
                      <td className="px-6 py-4 text-sm font-medium text-foreground">{formatMinor(d.amount)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground max-w-[200px] truncate" title={d.reason ?? ''}>{d.reason ?? '—'}</td>
                      <td className="px-6 py-4"><DisputeStatusBadge status={d.status as DisputeStatus} /></td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(d.createdAt)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(d.resolvedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'clawbacks' && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Recorded liabilities from lost post-settlement disputes. Phase 1 is record-only — recover from the merchant manually.
          </p>
          {clawbacksQuery.isLoading && <div className="text-center py-16 text-muted-foreground">Loading clawbacks…</div>}
          {clawbacksQuery.error && <div className="text-center py-16 text-red-500">Failed to load clawbacks.</div>}
          {!clawbacksQuery.isLoading && !clawbacksQuery.error && (clawbacksQuery.data?.length ?? 0) === 0 && (
            <div className="text-center py-16 text-muted-foreground">No clawback liabilities.</div>
          )}

          {!clawbacksQuery.isLoading && !clawbacksQuery.error && (clawbacksQuery.data?.length ?? 0) > 0 && (
            <div className="bg-card rounded-2xl border border-border overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Amount</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Period</th>
                    <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Recorded</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {clawbacksQuery.data!.map((c) => (
                    <tr key={c.id} className="hover:bg-muted transition-colors">
                      <td className="px-6 py-4 text-sm text-muted-foreground max-w-[160px] truncate" title={c.tenantId}>{merchantName(c.tenantId)}</td>
                      <td className="px-6 py-4 text-sm font-medium text-red-600">{formatMinor(c.amount)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground max-w-[280px] truncate" title={c.description}>{c.description}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{c.period}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showRefund && <RefundModal onClose={() => setShowRefund(false)} />}
    </div>
  );
}
